<template>
  <v-card class="pa-8" min-height="100%">
    <v-card-title class="text-h4 text-primary font-weight-regular">{{ $t('RegisterFormTitle') }}</v-card-title>
    <v-form ref="form" v-model="valid" @submit.prevent="register">
      <v-card-text>
        <v-text-field
          v-model="email"
          :label="$t('RegisterFormLabelName')"
          persistent-placeholder
          variant="outlined"
          :rules="rules"
        />

        <v-text-field
          v-model="password"
          :label="$t('RegisterFormLabelPassword')"
          persistent-placeholder
          variant="outlined"
          type="password"
          :rules="rules"
        />

        <v-text-field
          :label="$t('RegisterFormLabelPasswordRepeat')"
          persistent-placeholder
          type="password"
          variant="outlined"
          :rules="rulesPassword"
        />
      </v-card-text>
      <v-card-actions>
        <v-btn color="primary" :to="{ name: Routes.auth_login }" prepend-icon="mdi-arrow-left" class="flex-1-1-0">
          {{ $t('RegisterBack') }}
        </v-btn>
        <v-btn color="primary" :disabled="!valid" variant="flat" type="submit" class="flex-1-1-0 text-surface-bright">
          {{ $t('RegisterButton') }}
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, useTemplateRef } from 'vue'
import { Routes } from '~/_routes'
import { useHead } from 'nuxt/app'
import type { VForm } from 'vuetify/components'
import { useI18n } from 'vue-i18n'
import { definePageMeta } from '#imports'
import { GuestMode } from '~/lib/auth'
import { useConfirm } from '~/plugins/vue-plugins/use-dialog'
import useUser from '~/composables/useUser'
import { FormRules } from '~/helper/form-rules'
import { Notify } from '~/lib/notify'
export default defineComponent({
  name: 'RegisterPage',
  setup() {
    const form = useTemplateRef<InstanceType<typeof VForm>>('form')
    const { t } = useI18n()
    definePageMeta({
      auth: GuestMode,
    })
    useHead({
      title: t('PageRegister'),
    })
    const { auth } = useUser()
    return {
      form,
      Routes,
      auth,
      confirm: useConfirm(),
    }
  },
  data() {
    return {
      loading: false,
      valid: true,
      email: '',
      password: '',
    }
  },
  computed: {
    rules() {
      return [FormRules.required(this)]
    },
    rulesPassword() {
      return [FormRules.required(this), FormRules.passwordMatch(this.password, this)]
    },
  },
  methods: {
    async register() {
      this.valid = Boolean((await this.form?.validate())?.valid)
      if (!this.valid) {
        return
      }
      this.loading = true
      try {
        const result = await this.confirm({
          content: this.$t('RegisterPasswordInfo'),
          cancellationButtonProps: {
            color: 'primary',
            flat: false,
          },
        })

        if (!result) {
          return
        }

        const registerResponse = await this.$backend.authApi.register({
          admin: false,
          password: this.password,
          name: this.email,
          email: this.email,
        })
        Notify.success(this.$t('RegisterSuccess'))

        const formdata = new FormData()
        formdata.set('username', registerResponse.data.email)
        formdata.set('password', this.password)

        await this.auth.signIn(formdata, { external: true })
      } catch (e) {
        this.$handleFetchError(e)
      } finally {
        this.loading = false
      }
    },
  },
})
</script>
